import { IconButton } from '@chakra-ui/button';
import Icon from '@chakra-ui/icon';
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Flex,
  FormControl,
  FormLabel,
  Stack,
  Tooltip,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import React from 'react';
import { useForm } from 'react-hook-form';
import { FaRegTimesCircle } from 'react-icons/fa';
import { Button, ColorfulBar, Input } from '~/components';
import { saveDefaultDiscount } from '~/services/Discounts';
import { DefaultDiscount } from '~/types/discount';
import { formatCurrency } from '~/utils/formatCurrency';
import { queryClient } from '~/utils/react-query';

interface EditableRowDefaultDiscountProps {
  item: DefaultDiscount;
  handleDelete: (id: string | undefined) => void;
}

export const EditableRowDefaultDiscount: React.FC<EditableRowDefaultDiscountProps> =
  ({ item, handleDelete }: EditableRowDefaultDiscountProps) => {
    const toast = useToast();
    const [form, setForm] = React.useState({
      tempo_contrato: item.tempo_contrato,
      tempo_extensao: item.tempo_extensao,
      desconto_padrao: item.desconto_padrao,
      desconto_minimo: item.desconto_minimo,
      desconto_maximo: item.desconto_maximo,
    });

    const {
      isOpen: defaultConfirmIsOpen,
      onOpen: defaultConfirmOnOpen,
      onClose: defaultConfirmOnClose,
    } = useDisclosure();

    const {
      register,
      handleSubmit,
      setValue,
      reset,
      setError,
      formState,
      clearErrors,
      control,
    } = useForm();
    const { errors } = formState;

    const parse = (valueAsString: string) => {
      let valueAsNumber = 0;
      try {
        let parsedValueAsString = valueAsString.replace(/\D/g, '');
        parsedValueAsString = parsedValueAsString.padStart(2 + 1, '0');
        parsedValueAsString = `${parsedValueAsString.substring(
          0,
          parsedValueAsString.length - 2,
        )}.${parsedValueAsString.slice(2 * -1)}`;
        valueAsNumber = parseFloat(parsedValueAsString);
      } catch {
        valueAsNumber = 0;
      }
      return valueAsNumber;
    };

    const [descontoPadraoFormatado, setDescontoPadraoFormatado] =
      React.useState(
        formatCurrency(parse(String(item.desconto_padrao))).replace('R$', ''),
      );

    const [descontoMinimoFormatado, setDescontoMinimoFormatado] =
      React.useState(
        formatCurrency(parse(String(item.desconto_minimo))).replace('R$', ''),
      );

    const [descontoMaximoFormatado, setDescontoMaximoFormatado] =
      React.useState(
        formatCurrency(parse(String(item.desconto_maximo))).replace('R$', ''),
      );

    const isFormValid = () => {
      clearErrors();
      !form.tempo_contrato &&
        setError('tempoContrato', {
          type: 'required',
          message: 'Campo obrigatório',
        });

      !form.tempo_extensao &&
        setError('tempoExtensao', {
          type: 'required',
          message: 'Campo obrigatório',
        });

      Number(form.tempo_contrato) > 2147483647 &&
        setError('tempoContrato', {
          type: 'max',
          message: 'Valor inválido',
        });

      Number(form.tempo_extensao) > 2147483647 &&
        setError('tempoExtensao', {
          type: 'max',
          message: 'Valor inválido',
        });
      // verificar se os descontos podem ser zero
      !form.desconto_padrao &&
        setError('descontoPadrao', {
          type: 'required',
          message: 'Campo obrigatório',
        });

      !form.desconto_minimo &&
        setError('descontoMinimo', {
          type: 'required',
          message: 'Campo obrigatório',
        });

      Number(form.desconto_minimo) > Number(form.desconto_maximo) &&
        setError('descontoMinimo', {
          type: 'custom',
          message: "Valor não deve ser superior ao 'desconto máximo'",
        });

      !form.desconto_maximo &&
        setError('descontoMaximo', {
          type: 'required',
          message: 'Campo obrigatório',
        });

      const { _formState } = control;
      return _formState.isValid;
    };

    const salvar = () => {
      if (!isFormValid()) {
        return;
      }
      if (item.id && item.id?.toString().length !== 36) {
        if (
          form.tempo_contrato !== item.tempo_contrato ||
          form.tempo_extensao !== item.tempo_extensao ||
          Number(form.desconto_padrao) !== Number(item.desconto_padrao) ||
          Number(form.desconto_minimo) !== Number(item.desconto_minimo) ||
          Number(form.desconto_maximo) !== Number(item.desconto_maximo)
        ) {
          saveDefaultDiscount(item?.id, form)
            .then(() => {
              queryClient.invalidateQueries(['default-discounts']);
              toast({
                title: 'Desconto alterado',
                status: 'success',
                duration: 3000,
                isClosable: true,
                position: 'top-right',
              });
            })
            .catch(err => {
              toast({
                title: `${err.response.data.message}`,
                status: 'error',
                duration: 3000,
                isClosable: true,
                position: 'top-right',
              });
            });
        }
      } else if (
        form.tempo_contrato !== item.tempo_contrato &&
        form.tempo_extensao !== item.tempo_extensao &&
        form.desconto_padrao !== item.desconto_padrao &&
        form.desconto_minimo !== item.desconto_minimo &&
        form.desconto_maximo !== item.desconto_maximo
      ) {
        saveDefaultDiscount(null, form)
          .then(() => {
            queryClient.invalidateQueries(['default-discounts']);
            queryClient.invalidateQueries(['extension-discounts']);
            toast({
              title: 'Novo desconto criado',
              status: 'success',
              duration: 3000,
              isClosable: true,
              position: 'top-right',
            });
          })
          .catch(err => {
            toast({
              title: `${err.response.data.message}`,
              status: 'error',
              duration: 3000,
              isClosable: true,
              position: 'top-right',
            });
          });
      }
    };

    const handleTempoContrato = (e: React.FormEvent<HTMLInputElement>) => {
      const value = e.currentTarget.value.replace(/\D/g, '');
      setForm({
        ...form,
        tempo_contrato: Number(value),
      });
      clearErrors('tempoContrato');
    };

    const handleTempoExtensao = (e: React.FormEvent<HTMLInputElement>) => {
      const value = e.currentTarget.value.replace(/\D/g, '');
      setForm({
        ...form,
        tempo_extensao: Number(value),
      });
      clearErrors('tempoExtensao');
    };

    const handleDescontoPadrao = (e: React.FormEvent<HTMLInputElement>) => {
      const value = parse(e.currentTarget.value);
      setForm({
        ...form,
        desconto_padrao: value,
      });
      clearErrors('descontoPadrao');
      setDescontoPadraoFormatado(formatCurrency(value).replace('R$', ''));
    };

    const handleDescontoMinimo = (e: React.FormEvent<HTMLInputElement>) => {
      const value = parse(e.currentTarget.value);
      setForm({
        ...form,
        desconto_minimo: value,
      });
      clearErrors('descontoMinimo');
      setDescontoMinimoFormatado(formatCurrency(value).replace('R$', ''));
    };

    const handleDescontoMaximo = (e: React.FormEvent<HTMLInputElement>) => {
      const value = parse(e.currentTarget.value);
      setForm({
        ...form,
        desconto_maximo: value,
      });
      clearErrors('descontoMaximo');
      setDescontoMaximoFormatado(formatCurrency(value).replace('R$', ''));
    };

    return (
      <Stack p="2" direction="row" justifyContent="space-between">
        <FormControl isInvalid={errors.tempoContrato}>
          <FormLabel
            htmlFor="writeUpFile"
            style={{ fontSize: '14px' }}
            justifyContent="space-between"
            display="flex"
            whiteSpace="nowrap"
          >
            Tempo de contrato *
          </FormLabel>
          <Input
            name="tempoContrato"
            resize="none"
            error={errors?.tempoContrato}
            value={form.tempo_contrato}
            onBlur={salvar}
            onChange={e => handleTempoContrato(e)}
            fontSize="15px"
            width="100%"
            pattern="[0-9]*"
          />
        </FormControl>
        <FormControl isInvalid={errors.tempoExtensao}>
          <FormLabel
            htmlFor="writeUpFile"
            style={{ fontSize: '14px' }}
            justifyContent="space-between"
            display="flex"
            whiteSpace="nowrap"
          >
            Tempo de extensão *
          </FormLabel>
          <Input
            name="tempoExtensao"
            resize="none"
            error={errors?.tempoExtensao}
            value={form.tempo_extensao}
            onBlur={salvar}
            onChange={e => handleTempoExtensao(e)}
            fontSize="15px"
            width="100%"
            pattern="[0-9]*"
          />
        </FormControl>
        <FormControl isInvalid={errors.descontoPadrao}>
          <FormLabel
            htmlFor="writeUpFile"
            whiteSpace="nowrap"
            style={{ fontSize: '14px' }}
            justifyContent="space-between"
            display="flex"
          >
            % Desconto padrão *
          </FormLabel>
          <Input
            name="descontoPadrao"
            resize="none"
            error={errors?.descontoPadrao}
            value={descontoPadraoFormatado}
            onChange={e => handleDescontoPadrao(e)}
            onBlur={salvar}
            fontSize="15px"
            width="100%"
          />
        </FormControl>
        <FormControl isInvalid={errors.descontoMinimo}>
          <FormLabel
            htmlFor="writeUpFile"
            style={{ fontSize: '14px' }}
            justifyContent="space-between"
            display="flex"
            whiteSpace="nowrap"
          >
            % Desconto mínimo *
          </FormLabel>
          <Input
            name="descontoMinimo"
            resize="none"
            error={errors?.descontoMinimo}
            value={descontoMinimoFormatado}
            onChange={e => handleDescontoMinimo(e)}
            onBlur={salvar}
            fontSize="15px"
            width="100%"
          />
        </FormControl>
        <FormControl isInvalid={errors.descontoMaximo}>
          <FormLabel
            htmlFor="writeUpFile"
            style={{ fontSize: '14px' }}
            justifyContent="space-between"
            display="flex"
            whiteSpace="nowrap"
          >
            % Desconto máximo *
          </FormLabel>
          <Input
            name="descontoMaximo"
            resize="none"
            error={errors?.descontoMaximo}
            value={descontoMaximoFormatado}
            onChange={e => handleDescontoMaximo(e)}
            onBlur={salvar}
            fontSize="15px"
            width="100%"
          />
        </FormControl>
        <Flex h="auto" alignItems={formState.isValid ? 'end' : 'center'}>
          <Tooltip
            label="Excluir desconto"
            marginBottom={formState.isValid ? 2 : ''}
          >
            <IconButton
              colorScheme="gray"
              background="transparent"
              _hover={{
                background: 'transparent',
              }}
              aria-label="delete"
              icon={<Icon as={FaRegTimesCircle} w={6} h={6} />}
              onClick={defaultConfirmOnOpen}
            />
          </Tooltip>
        </Flex>
        <AlertDialog
          motionPreset="slideInBottom"
          leastDestructiveRef={undefined}
          onClose={defaultConfirmOnClose}
          isOpen={defaultConfirmIsOpen}
          isCentered
          closeOnOverlayClick={false}
          size="xl"
        >
          <AlertDialogOverlay />

          <AlertDialogContent width={800}>
            <ColorfulBar />
            <AlertDialogHeader>Atenção</AlertDialogHeader>
            <AlertDialogBody>Deseja excluir este desconto?</AlertDialogBody>
            <AlertDialogFooter>
              <Button
                onClick={() => {
                  defaultConfirmOnClose();
                }}
                variant="secondary"
                width={100}
              >
                Cancelar
              </Button>
              <Button
                onClick={() => {
                  handleDelete(`${item?.id}`);
                  defaultConfirmOnClose();
                }}
                variant="primary"
                width={100}
                ml={3}
              >
                OK
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </Stack>
    );
  };
